/**
 * BranchContext
 *
 * Holds the active branch (office) and active cut-off period selection used
 * across the WIP System module's topbar (Branch/Office Switcher, Cut-Off
 * Selector). Persisted to localStorage under the same key apiClient.js
 * reads for the X-Branch-ID header, so switching branch here immediately
 * scopes every subsequent API call.
 *
 * Fetches the real branch list from the SSS backend (GET /branches)
 * itself -- only enabled once the user is authenticated, since the app
 * shell mounts BranchProvider above ProtectedRoute.
 */
import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sssApiClient, WIP_STORAGE_KEYS } from '../services/apiClient.js';
import { useAuth } from '../hooks/useAuth.js';
import { BranchContext, currentCutOffPeriod } from './branchContextInstance.js';

/** @typedef {{ id: string, name: string, code: string|null, parentId: string|null }} Branch */
/** @typedef {import('./branchContextInstance.js').CutOffPeriod} CutOffPeriod */

const CUTOFF_STORAGE_KEY = 'active_cutoff_period';

/** @param {{ children: import('react').ReactNode }} props */
export const BranchProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const [storedBranchId, setStoredBranchId] = useState(
    () => localStorage.getItem(WIP_STORAGE_KEYS.ACTIVE_BRANCH) ?? null
  );
  const [activeCutOff, setActiveCutOffState] = useState(
    /** @type {CutOffPeriod} */ (localStorage.getItem(CUTOFF_STORAGE_KEY) ?? currentCutOffPeriod())
  );

  const { data: rawBranches } = useQuery({
    queryKey: ['sss', 'branches'],
    queryFn: async ({ signal }) => {
      const { data } = await sssApiClient.get('/branches', { signal });
      return Array.isArray(data) ? data : (data?.data ?? []);
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  /** @type {Branch[]} */
  const branches = useMemo(
    () =>
      (rawBranches ?? []).map((b) => ({
        id: String(b.id),
        name: b.name,
        code: b.code ?? null,
        parentId: b.parent_id ? String(b.parent_id) : null,
      })),
    [rawBranches]
  );

  // Defaults to the first branch once the real branch list arrives, if
  // none has been explicitly picked yet -- computed during render rather
  // than synced via an effect+setState, since it's a pure function of
  // (storedBranchId, branches) with no external side effect to perform.
  const activeBranchId = storedBranchId ?? branches[0]?.id ?? null;

  const setActiveBranchId = useCallback((branchId) => {
    setStoredBranchId(branchId);
    if (branchId) {
      localStorage.setItem(WIP_STORAGE_KEYS.ACTIVE_BRANCH, branchId);
    } else {
      localStorage.removeItem(WIP_STORAGE_KEYS.ACTIVE_BRANCH);
    }
  }, []);

  const setActiveCutOff = useCallback((period) => {
    setActiveCutOffState(period);
    localStorage.setItem(CUTOFF_STORAGE_KEY, period);
  }, []);

  const value = useMemo(
    () => ({
      branches,
      activeBranchId,
      setActiveBranchId,
      activeCutOff,
      setActiveCutOff,
    }),
    [branches, activeBranchId, setActiveBranchId, activeCutOff, setActiveCutOff]
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};
